import React, { Component, ComponentType, FC } from 'react'
import { Box, CircularProgress, Collapse, IconButton, InputAdornment, styled, TextField, TextFieldProps } from '@mui/material'
import ContentPasteIcon from '@mui/icons-material/ContentPaste'
import { mergeObjects } from '../utils'
import { IFormInputBase } from './types'
import { getErrorMessage } from './helpers'
import { ImageWithFallback, IImageWithFallbackPropsOwner } from '../components/image-with-fallback'
import { IRichTooltipPanelConfig, RichTooltip, HelpOutlinePulseIcon } from '../components/rich-tooltip'

export interface InputBaseImage<T> extends Omit<Partial<IImageWithFallbackPropsOwner>, 'src'> {
  src?: string | ((value: any, model?: Partial<T>) => string | undefined)
  element?: React.ComponentType<{ value: any; model?: Partial<T> }>
  mirror?: boolean
}

export interface IInputSlots<T> {
  maxLength?: number
  textFieldProps?: TextFieldProps
  pastenable?: boolean
  imageLeft?: InputBaseImage<T>
  imageRight?: InputBaseImage<T>
  tooltip?: IRichTooltipPanelConfig
}

export interface IInputProps<T> extends IFormInputBase<T, IInputSlots<T>> {}

export interface IInputState {
  value?: string
}

export interface IInputParams<T> extends IInputSlots<T> {}

const createInput = function <T>(params?: IInputParams<T>): ComponentType<IInputProps<T>> {
  class InputBase extends Component<IInputProps<T>, IInputState> {
    private _cachedSlots: IInputSlots<T> = {}
    constructor(props: IInputProps<T>) {
      super(props)
      this._cachedSlots = this.mergeSlots(props.slots) ?? {}
      this.state = { value: this.defaulValue }
    }

    get slots() {
      return this._cachedSlots
    }

    get defaulValue(): string {
      const { data, name } = this.props
      return (!!data && !!name ? data[name]?.toString() : undefined) ?? this.props.defaultValue
    }

    componentDidUpdate(prevProps: IInputProps<T>) {
      if (prevProps.slots !== this.props.slots) {
        this._cachedSlots = this.mergeSlots(this.props.slots)
      }
    }

    shouldComponentUpdate(nextProps: Readonly<IInputProps<T>>): boolean {
      const { name, slots } = this.props
      if (!!name) {
        const currentDataValue = this.props.data?.[name]?.toString() ?? ''
        const nextDataValue = nextProps.data?.[name]?.toString() ?? ''
        if (currentDataValue !== nextDataValue) {
          this.setState({ value: nextDataValue })
          return false
        }
      }

      if (slots !== nextProps.slots) {
        return true
      }
      return true
    }

    mapTextFieldProps = (): TextFieldProps => {
      const errorMessage = getErrorMessage(this.props.messageErrors, this.props.name)
      const tfp: TextFieldProps = {
        fullWidth: true,
        variant: 'outlined',
        name: this.props.name?.toString(),
        error: errorMessage.error,
        helperText: errorMessage.message,
        disabled: this.props.disabled,
        onBlur: () => {
          if (!this.props.name) return
          this.props.onBlur && this.props.onBlur(this.props.name)
        },
        label: this.getLabel(),
        placeholder: this.props.placeholder,
        InputLabelProps: !!this.state.value ? { shrink: true } : {},
        value: this.state.value ?? '',
        onChange: this.handleChange
      }
      if (!tfp.InputProps) tfp.InputProps = {}
      tfp.InputProps.endAdornment = (
        <InputAdornment position='end'>
          {this.slots.pastenable && (
            <IconButton onClick={this.handlePaste} edge='end'>
              <ContentPasteIcon />
            </IconButton>
          )}
          {this.slots.tooltip && (
            <RichTooltip panel={this.slots.tooltip}>
              <HelpOutlinePulseIcon fontSize='small' />
            </RichTooltip>
          )}
        </InputAdornment>
      )
      if (this.slots?.maxLength) tfp.inputProps = { ...tfp.inputProps, maxLength: this.slots.maxLength }
      return mergeObjects<TextFieldProps>({}, tfp, this.slots?.textFieldProps)
    }

    //#region Render
    render() {
      return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          {!!this.props.disabled && <input hidden name={this.props.name?.toString()} defaultValue={this.defaulValue} />}
          {this.renderImageSide('left')}
          <TextField {...this.mapTextFieldProps()} />
          {this.renderImageSide('right')}
        </Box>
      )
    }

    renderImageSide = (side: 'left' | 'right') => {
      const image = side === 'left' ? this.slots.imageLeft : this.slots.imageRight
      if (!image) return <></>
      const { src, element, alt = 'input-image', fallbackSrc = '', debounceDelay = 700, ...imageOther } = image
      if (element) {
        const Element = element
        return (
          <WrapImage>
            <Element value={this.state.value} model={this.props.data} />
          </WrapImage>
        )
      }
      const finalSrc = typeof src === 'function' ? src(this.state.value, this.props.data) : (src ?? this.state.value)
      const imageProps: IImageWithFallbackPropsOwner = { src: finalSrc, alt, fallbackSrc, debounceDelay }
      return (
        <Collapse sx={{ mx: '10px' }} in={!!finalSrc} unmountOnExit orientation='horizontal'>
          <WrapImage>
            <ImageWithFallback {...imageOther} {...imageProps} loading={<LoadingCircularProgress />} />
          </WrapImage>
        </Collapse>
      )
    }
    //#endregion

    private mergeSlots = (currentSlots?: IInputSlots<T>): IInputSlots<T> => {
      const base = mergeObjects<IInputSlots<T>>({}, params, currentSlots)
      const { imageLeft, imageRight } = base

      // mirror from left to right
      if (imageLeft?.mirror && !imageRight) {
        base.imageRight = { ...imageLeft }
        delete base.imageRight.mirror
      }
      // mirror from right to left
      else if (imageRight?.mirror && !imageLeft) {
        base.imageLeft = { ...imageRight }
        delete base.imageLeft.mirror
      }

      return base
    }

    getLabel = () => {
      if (!this.props.label) return
      if (!this.slots?.maxLength) return this.props.label
      return `${this.props.label} (${this.state.value?.length ?? 0}/${this.slots.maxLength})`
    }

    handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      this.setState({ value: event.target.value })
    }

    handlePaste = async () => {
      try {
        const clipboardText = await navigator.clipboard.readText()
        this.setState({ value: clipboardText })
      } catch (error) {
        console.error('Error clipboard:', error)
      }
    }
  }
  return InputBase
}

export default createInput

// export type InputTextType<T> = ReturnType<typeof CreateInputBase<T>>

const LoadingCircularProgress: FC = () => (
  <div>
    <CircularProgress size={24} />
  </div>
)

const WrapImage = styled(Box)({
  fontSize: '1rem',
  height: 'var(--input-base-image-size, 54px)',
  width: 'var(--input-base-image-size, 54px)',
  position: 'relative',
  borderRadius: '8px',
  boxShadow: 'rgba(0, 0, 0, 0.16) 0px 1px 4px',
  overflow: 'hidden',
  '& > img': {
    position: 'absolute',
    top: 0,
    left: 0,
    backgroundRepeat: 'no-repeat',
    height: '100%',
    width: '100%',
    backgroundSize: 'contain'
  },
  '& > div': {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    width: '100%',
    background: '#fafafa',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
})
